/**
 * Surface-Cp metrics (FXC-9009 design-opt core, §8/§B1 of the design study). Pure functions over a
 * per-element surface-pressure strip: `{ upper:[[x/c,Cp],…], lower:[[x/c,Cp],…] }` (the shape of
 * `SurfaceCpElement` from FlowVisLayer — `upper` = suction side, `lower` = pressure side, both sorted
 * by x/c). These are the cheap design quantities the inverse-design / optimizer loop scores against:
 *   - peakSuction      — most-negative Cp and where it sits (shock/load proxy).
 *   - pressureRecovery — dCp/dx from peak suction to the TE on the suction side (separation proxy;
 *                        more positive ⇒ stronger adverse gradient ⇒ closer to separating).
 *   - teLoading        — trailing-edge loading Cp_lower − Cp_upper (aft loading / Kutta proxy).
 *   - cpMatchResidual  — Cp − Cp_target on a common x-grid over an optional region (inverse-design
 *                        least-squares residual; mixed-inverse limits it to a [x0,x1] band).
 *   - flowAngleDeg     — local flow angle atan2(v,u) in degrees (slot-alignment quantity).
 *
 * No NN weights needed — everything is a pure function of sampled Cp, so it unit-tests on synthetic
 * curves (metrics.test.ts).
 */

/** A point on a Cp strip: [x/c, Cp]. */
export type CpPoint = [number, number];

/** Minimal surface-Cp strip — structurally compatible with FlowVisLayer's `SurfaceCpElement`. */
export interface SurfaceCp {
  upper: CpPoint[];
  lower: CpPoint[];
}

/** Linear interpolation of Cp at x on a strip sorted ascending by x; clamps outside the data range. */
function interpAt(curve: CpPoint[], x: number): number {
  const n = curve.length;
  if (n === 0) return NaN;
  if (x <= curve[0]![0]) return curve[0]![1];
  if (x >= curve[n - 1]![0]) return curve[n - 1]![1];
  for (let i = 1; i < n; i++) {
    const x1 = curve[i]![0];
    if (x <= x1) {
      const x0 = curve[i - 1]![0];
      const c0 = curve[i - 1]![1];
      const c1 = curve[i]![1];
      const dx = x1 - x0;
      return dx > 0 ? c0 + ((x - x0) / dx) * (c1 - c0) : c0;
    }
  }
  return curve[n - 1]![1];
}

/** Least-squares slope of Cp vs x over a (sub)array of points; 0 if fewer than two distinct x. */
function lsqSlope(pts: CpPoint[]): number {
  const n = pts.length;
  if (n < 2) return 0;
  let mx = 0;
  let my = 0;
  for (const p of pts) {
    mx += p[0];
    my += p[1];
  }
  mx /= n;
  my /= n;
  let cov = 0;
  let varx = 0;
  for (const p of pts) {
    const dx = p[0] - mx;
    cov += dx * (p[1] - my);
    varx += dx * dx;
  }
  return varx > 0 ? cov / varx : 0;
}

/**
 * Peak suction = the most-negative Cp anywhere on the element (both surfaces scanned) and its x/c.
 * Returns `{ cp: +Infinity, x: NaN }` for an empty strip.
 */
export function peakSuction(surf: SurfaceCp): { cp: number; x: number } {
  let cp = Infinity;
  let x = NaN;
  for (const curve of [surf.upper, surf.lower]) {
    for (const p of curve) {
      if (p[1] < cp) {
        cp = p[1];
        x = p[0];
      }
    }
  }
  return { cp, x };
}

/**
 * Pressure-recovery slope: least-squares dCp/dx on the suction (upper) surface from the peak-suction
 * station to the trailing edge. More positive ⇒ steeper adverse recovery ⇒ stronger separation risk.
 * Returns 0 if the recovery region has fewer than two points.
 */
export function pressureRecovery(surf: SurfaceCp): number {
  const u = surf.upper;
  if (u.length < 2) return 0;
  let iMin = 0;
  for (let i = 1; i < u.length; i++) {
    if (u[i]![1] < u[iMin]![1]) iMin = i;
  }
  return lsqSlope(u.slice(iMin));
}

/** Trailing-edge loading: Cp_lower(TE) − Cp_upper(TE), using the aft-most (max-x) point of each side. */
export function teLoading(surf: SurfaceCp): number {
  const u = surf.upper;
  const l = surf.lower;
  if (u.length === 0 || l.length === 0) return NaN;
  return l[l.length - 1]![1] - u[u.length - 1]![1];
}

/** Overlapping x-range across both sides of both strips (where all four curves carry data). */
function commonRange(a: SurfaceCp, b: SurfaceCp): [number, number] {
  let lo = -Infinity;
  let hi = Infinity;
  for (const c of [a.upper, a.lower, b.upper, b.lower]) {
    if (c.length === 0) continue;
    lo = Math.max(lo, c[0]![0]);
    hi = Math.min(hi, c[c.length - 1]![0]);
  }
  return [lo, hi];
}

/**
 * Inverse-design residual r = Cp − Cp_target sampled on a common, uniform x-grid of `n` stations,
 * concatenated [upper residuals…, lower residuals…]. With `region = [x0,x1]` the grid is confined to
 * that band (mixed-inverse over a targeted surface region); otherwise it spans the x-overlap of all
 * four curves. `surf` and `target` are linearly interpolated onto the grid (clamped at the ends).
 */
export function cpMatchResidual(
  surf: SurfaceCp,
  target: SurfaceCp,
  region?: [number, number],
  n = 41,
): number[] {
  let [x0, x1] = region ?? commonRange(surf, target);
  if (!(x1 > x0)) return [];
  const m = Math.max(2, Math.floor(n));
  const up: number[] = [];
  const lo: number[] = [];
  for (let i = 0; i < m; i++) {
    const x = x0 + ((x1 - x0) * i) / (m - 1);
    up.push(interpAt(surf.upper, x) - interpAt(target.upper, x));
    lo.push(interpAt(surf.lower, x) - interpAt(target.lower, x));
  }
  return [...up, ...lo];
}

/** Local flow angle in degrees: atan2(v, u). 0 = streamwise (+x), +90 = +z. */
export function flowAngleDeg(u: number, v: number): number {
  return (Math.atan2(v, u) * 180) / Math.PI;
}
